import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Users, User as UserIcon, Mail, AlertCircle } from "lucide-react";

/**
 * Par-innboks: aggregert visning av samtaler som tilhører paret + partneres
 * DMs hvis de aktivt har delt dem. Aktivering må skje fra hver partners
 * singel-konto under Innstillinger → Meldinger.
 */
export default function ParInnboks() {
  const { user, linkedCouple } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!linkedCouple?.id) return;
    api.get(`/couples/${linkedCouple.id}/inbox`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Kunne ikke laste par-innboks."));
  }, [linkedCouple?.id]);

  if (!user || user === false) {
    return <Layout><div className="font-meta">Logg inn for å se par-innboksen.</div></Layout>;
  }
  if (!linkedCouple) {
    return <Layout>
      <div className="text-center py-16">
        <div className="font-fraunces italic text-2xl text-[var(--cream)]">Ingen aktiv par-profil</div>
        <p className="font-manrope text-sm text-[var(--cream-soft)] mt-2">
          Du må være en del av en aktiv par-profil for å se felles innboks.
        </p>
        <Link to="/par-innstillinger" className="btn-magenta inline-block mt-4">Opprett eller inviter par</Link>
      </div>
    </Layout>;
  }

  return (
    <Layout>
      <div className="font-meta mb-4">
        <Link to="/par-innstillinger" className="hover:text-[var(--cream)]">Par-innstillinger</Link>
        <span className="mx-2">/</span>
        <span className="text-[var(--cream)]">Felles innboks</span>
      </div>
      <header className="mb-6">
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-2">Felles innboks</h1>
        <p className="font-manrope text-sm text-[var(--cream-soft)]">
          {linkedCouple.name} · samler par-DMs + DMs fra partnere som har valgt å dele
        </p>
      </header>

      {/* Status på deling */}
      {data?.share_state && (
        <div className="border border-white/10 p-4 mb-6 bg-white/[0.025]" data-testid="share-state">
          <div className="font-meta text-[10px] mb-2">DELINGSSTATUS</div>
          <div className="grid grid-cols-2 gap-3 text-xs font-manrope">
            <div className={`p-2 ${data.share_state.partner_a_shares ? "text-emerald-400" : "text-[var(--cream-faint)]"}`}>
              Partner A: {data.share_state.partner_a_shares ? "Deler DMs" : "Privat"}
            </div>
            <div className={`p-2 ${data.share_state.partner_b_shares ? "text-emerald-400" : "text-[var(--cream-faint)]"}`}>
              Partner B: {data.share_state.partner_b_shares ? "Deler DMs" : "Privat"}
            </div>
          </div>
          <p className="font-meta text-[10px] text-[var(--cream-faint)] mt-3 max-w-lg">
            Hver partner styrer selv om DMs deles, fra Innstillinger → Meldinger på deres egen singel-konto.
          </p>
        </div>
      )}

      {error && (
        <div className="border-l-2 border-[var(--magenta)] bg-[var(--magenta)]/10 p-4 text-sm text-[var(--cream)] mb-6 inline-flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-[var(--magenta)]" />
          {error}
        </div>
      )}

      {!data ? (
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      ) : data.conversations.length === 0 ? (
        <div className="text-center py-12 border border-white/10 rounded-sm bg-white/[0.015]">
          <Mail className="h-8 w-8 mx-auto mb-3 text-[var(--cream-faint)]" />
          <div className="font-fraunces italic text-xl text-[var(--cream)]">Ingen samtaler å vise enda</div>
          <p className="font-manrope text-xs text-[var(--cream-soft)] mt-2 max-w-md mx-auto">
            Når noen sender DM til paret, eller en partner aktiverer DM-deling, vil samtalene dukke opp her.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-white/5 border border-white/10 rounded-sm bg-white/[0.025]" data-testid="par-inbox-list">
          {data.conversations.map((c) => (
            <li key={c.id} className="hover:bg-white/[0.02] transition-colors">
              <Link to={`/messages/${c.id}`} className="flex items-center gap-4 px-5 py-4" data-testid={`par-inbox-row-${c.id}`}>
                <div className="shrink-0">
                  {c.side === "couple" ? (
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-sm bg-[var(--magenta)] text-black">
                      <Users className="h-4 w-4" />
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-sm bg-white/10 text-[var(--cream)]">
                      <UserIcon className="h-4 w-4" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-fraunces italic text-[var(--cream)] truncate">{c.other?.username || "ukjent"}</span>
                    <span className="font-meta text-[10px] text-[var(--cream-faint)]">
                      → {c.side === "couple" ? "PARET" : `@${c.side_username || "?"}`}
                    </span>
                  </div>
                  <div className="font-meta text-[10px] mt-0.5">{formatRelative(c.last_message_at)}</div>
                </div>
                {c.unread > 0 && (
                  <span className="bg-[var(--magenta)] text-black text-[10px] font-bold px-2 py-0.5 rounded-sm">
                    {c.unread}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Layout>
  );
}
